import {
  ButtonGroup,
  Button,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import './styles.css';
import dayjs from 'dayjs';
import { useAppSelector } from 'store/hook';
import ProductionSelector from 'views/Production/components/Selector/Selector';
import CustomDateRangePicker from 'components/DatePIcker/CustomDateRangePicker';
import { getStartAndEndDateFromRange } from 'views/FuelUsage/utils';
import { useState, useEffect } from 'react';
import { calculateDaysDifference } from 'views/FuelAnalytics/IdleDashboard/utils';
import { useTranslation } from 'react-i18next';
import useShiftConfig from 'hooks/useShiftConfig';
import { formatShiftHour } from 'utils/helpers/customerShiftHelpers';
import StyledSwitchButton from 'components/Buttons/SwitchButton';
import useCustomerInfo from 'hooks/useCustomerInfo';
import dayjs_tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
dayjs.extend(dayjs_tz);

interface Props {
  enableDarkTheme: boolean;
  ignoreYtd?: boolean;
  isSelected: string;
  setIsSelected: any;
  handleDateChange: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  yesterday: any;
  aggregation: string;
  setAggregation: any;
  shiftWiseAnalysis: boolean;
  setShiftWiseAnalysis: any;
  shiftNumber: number;
  setShiftNumber: any;
  handleFetchCustomDateRangeData: ({
    aggregation,
  }: {
    aggregation: string;
  }) => void;
  handleFetchCustomOnAggregationChange: ({
    aggregation,
    startDate,
    endDate,
  }: {
    aggregation: string;
    startDate: Date;
    endDate: Date;
  }) => void;
}

const DateRangeDiv = ({
  enableDarkTheme,
  ignoreYtd = false,
  isSelected,
  setIsSelected,
  handleDateChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  yesterday,
  aggregation,
  setAggregation,
  shiftWiseAnalysis,
  setShiftWiseAnalysis,
  shiftNumber,
  setShiftNumber,
  handleFetchCustomDateRangeData,
  handleFetchCustomOnAggregationChange,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { customerTz: timezone } = useCustomerInfo();
  const { shiftOptions } = useShiftConfig();
  const { loadingStates } = useAppSelector((state) => state.productionReducer);

  const isLoading =
    loadingStates.isLoadingFleetAssets ||
    loadingStates.isLoadingFleetSummary ||
    loadingStates.isLoadingFleetDetails ||
    loadingStates.isLoadingFleetPeriodSummary ||
    loadingStates.isLoadingSuggestions;

  const [aggregationOptions, setAggregationOptions] = useState([
    'Daily',
    'Monthly',
  ]);

  const handleSelect = (id: string) => {
    setIsSelected(id);

    let newStartDate = startDate;
    let newEndDate = yesterday;

    switch (id) {
      case 'Today': {
        const startDateVal = new Date().toISOString().slice(0, 10);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(startDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case '1D': {
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(1);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Daily');
        break;
      }

      case '7D': {
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(7);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case '14D': {
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(14);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Daily');
        break;
      }

      case '1M': {
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(30);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case 'Custom':
        // Do nothing, user will select manually
        break;
      default:
        break;
    }

    // Update the startDate and endDate for non-custom ranges
    if (id !== 'Custom') {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    }
  };

  let dateRangeList = ['Today', '1D', '7D', '14D', '1M', 'Custom'];
  if (ignoreYtd) {
    dateRangeList = dateRangeList.filter((item) => item !== 'YTD');
  }

  const dateRangeLabel = (item: string): string => {
    const map: Record<string, string> = {
      Today: t('date_range_today'),
      '1D': t('date_range_1d'),
      '7D': t('date_range_7d'),
      '14D': t('date_range_14d'),
      '1M': t('date_range_1m'),
      Custom: t('date_range_custom'),
    };
    return map[item] ?? item;
  };

  const aggregationLabel = (item: string): string => {
    const map: Record<string, string> = {
      Daily: t('aggregation_daily'),
      Monthly: t('aggregation_monthly'),
    };
    return map[item] ?? item;
  };

  const rangeIsOverMonth = ['Custom'].includes(isSelected);

  useEffect(() => {
    if (!startDate || !endDate) return;
    const days = calculateDaysDifference(startDate, endDate);
    if (days >= 30) {
      setAggregationOptions(['Daily', 'Monthly']);
    } else {
      if (aggregation === 'Monthly') {
        setAggregation('Daily');
      }
      setAggregationOptions(['Daily']);
    }
  }, [rangeIsOverMonth, startDate, endDate]);

  return (
    <>
      <Typography
        style={{
          fontFamily: "'Inter Variable', sans-serif",
          margin: '20px 0',
          backgroundColor: palette.pageBackground,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <div
            style={{
              color: enableDarkTheme
                ? ThemePalette.typography.mediumGray
                : '#3d3d3d',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Typography style={{ fontSize: '12px', marginRight: '10px' }}>
              {t('quick_range_10')}
            </Typography>
            <ButtonGroup
              size="small"
              variant="outlined"
              aria-label={t('basic_button_group')}
              sx={{
                border: `1px solid ${palette.primary}`,
                '& .MuiButton-root': {
                  borderColor: `${palette.primary} !important`,
                },
              }}
            >
              {dateRangeList.map((item) => {
                return (
                  <Button
                    key={item}
                    size="small"
                    disabled={isLoading}
                    style={{
                      backgroundColor:
                        isSelected === item
                          ? palette.primary
                          : enableDarkTheme
                          ? palette.boxBackground
                          : palette.toolBarBackground,
                      color: isLoading
                        ? ThemePalette.typography.grey
                        : isSelected === item
                        ? palette.white
                        : enableDarkTheme
                        ? palette.textPrimary
                        : palette.textPrimary,
                    }}
                    onClick={() => {
                      handleSelect(item);
                    }}
                  >
                    {dateRangeLabel(item)}
                  </Button>
                );
              })}
            </ButtonGroup>
          </div>
          {isSelected === 'Custom' && (
            <div
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.mediumGray
                  : '#3d3d3d',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              {aggregation === 'Monthly' ? (
                <CustomDateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onFinalDateChange={handleDateChange}
                  maxDate={dayjs(yesterday)}
                  format="MMM YYYY"
                  disabled={isSelected !== 'Custom' || isLoading}
                  views={['month', 'year']}
                  sx={{
                    minWidth: '148px',
                    maxWidth: '148px',
                    width: '148px',
                  }}
                  autoOpenEnabled={false}
                />
              ) : (
                <CustomDateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onFinalDateChange={handleDateChange}
                  maxDate={dayjs(yesterday)}
                  sx={{
                    minWidth: '148px',
                    maxWidth: '148px',
                    width: '148px',
                  }}
                  disabled={isSelected !== 'Custom' || isLoading}
                  autoOpenEnabled={false}
                />
              )}
              <Button
                onClick={() => {
                  handleFetchCustomDateRangeData({ aggregation });
                }}
                disabled={
                  isLoading || isSelected !== 'Custom' || !startDate || !endDate
                }
                sx={{
                  fontSize: '11px',
                  backgroundColor: palette.primary,
                  color: '#fff',
                  padding: '5px 10px',
                  borderRadius: '5px',
                  border: `1px solid ${palette.primary}`,
                  '&:hover': {
                    backgroundColor: palette.primaryDark,
                    color: '#fff',
                  },
                }}
              >
                {t('apply')}
              </Button>
            </div>
          )}
          <div
            style={{
              color: enableDarkTheme
                ? ThemePalette.typography.mediumGray
                : '#3d3d3d',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Typography style={{ fontSize: '11px' }}>
              {t('shift_wise_analysis')}
            </Typography>
            <StyledSwitchButton
              disableRipple
              checked={shiftWiseAnalysis}
              disabled={loadingStates?.isLoadingFleetSummary}
              onChange={(e) => {
                setShiftWiseAnalysis(e.target.checked);
              }}
              color="secondary"
            />
          </div>
          {shiftWiseAnalysis && (
            <Select
              value={shiftNumber}
              defaultValue={shiftNumber}
              disabled={loadingStates?.isLoadingFleetSummary}
              onChange={(e) => {
                setShiftNumber(e.target.value);
                handleFetchCustomDateRangeData({ aggregation });
              }}
              sx={{
                height: '30px',
                fontSize: '11px',
                marginLeft: '10px',
                marginRight: '20px',
                backgroundColor: enableDarkTheme
                  ? palette.cartesianGrid
                  : palette.menuOptions,
                color: enableDarkTheme
                  ? palette.textPrimary
                  : palette.textPrimary,
                '& .MuiSelect-icon': {
                  color: palette.primary,
                },
                '& fieldset': {
                  borderColor: palette.borderGrey,
                },
                '&.MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: palette.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: palette.primary,
                  },
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    backgroundColor: palette.menuOptions,
                    color: palette.textPrimary,
                    fontSize: '11px',
                  },
                },
              }}
            >
              {shiftOptions.map((shift, index) => {
                const todayLocal = dayjs().tz(timezone);
                const startTimeStr = formatShiftHour(
                  shift.startHour,
                  todayLocal
                );
                const endTimeStr = formatShiftHour(shift.endHour, todayLocal);
                const label = shift.shiftName ?? `Shift ${index + 1}`;
                return (
                  <MenuItem
                    key={index}
                    value={index + 1}
                    sx={{ fontSize: '11px' }}
                  >
                    {`${label} ${startTimeStr} - ${endTimeStr}`}
                  </MenuItem>
                );
              })}
            </Select>
          )}
          {rangeIsOverMonth && (
            <div
              style={{
                marginLeft: '10px',
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  color: palette.textPrimary,
                  marginTop: '7px',
                  marginRight: '10px',
                }}
              >
                Aggregation
              </Typography>

              <ProductionSelector
                options={aggregationOptions}
                getOptionLabel={aggregationLabel}
                selected={aggregation}
                setSelected={(value: string) => {
                  setAggregation(value);
                  if (isSelected === 'Custom') {
                    let sD = dayjs(startDate).toDate();
                    let eD = dayjs(endDate).toDate();
                    if (value === 'Monthly') {
                      const today = dayjs();

                      const isCurrentMonth =
                        dayjs(endDate).month() === today.month() &&
                        dayjs(endDate).year() === today.year();

                      sD = dayjs(startDate).startOf('month').toDate();

                      eD = isCurrentMonth
                        ? today.toDate()
                        : dayjs(endDate).endOf('month').toDate();
                    }
                    handleFetchCustomOnAggregationChange({
                      aggregation: value,
                      startDate: sD,
                      endDate: eD,
                    });
                  }
                }}
                minWidth="120px"
                disabled={loadingStates?.isLoadingFleetSummary}
              />
            </div>
          )}
        </div>
      </Typography>
    </>
  );
};

export default DateRangeDiv;
